import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { appConfigStore } from 'Models/AppConfigStore';
import { store } from 'Models/Store';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { findAllMeasurementsForFerryTrip, getMeasurementValueFromId } from 'Util/_HumanWritten/MeasurementUtils';
import {
	convertWeight,
	EXACT_WEIGHT_MAX_STORABLE,
	parseWeightUnit,
	pickWeightBand,
	roundWeightForUnit,
	toCanonicalKg,
	weightUnitLabel,
} from 'Util/Weight';
import { NumberInput } from 'Views/Components/New/NumberInput';
import { BookingWizardData } from '../../BookingWizardData/BaseBookingWizardData';
import { isVehicleBookingWizardData, VehicleBookingWizardData } from '../../BookingWizardData/VehicleBookingWizardData';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { WizardErrors } from '../../BookingWizardTypes';
import { useVehicleTabContext } from './VehicleTabContext';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';

export function VehicleExactWeightInput() {
	const intl = useIntl();
	const { cargoTypes } = useVehicleTabContext();

	const { wizardData, setWizardData, innerWizardErrors, setInnerWizardErrors } = useBookingWizardDataStore();

	const displayUnit = appConfigStore.weightDisplayUnit;

	const data = wizardData.innerWizardData as VehicleBookingWizardData;
	const cargoType = useMemo(() => cargoTypes.find(x => x.id === data?.cargoTypeId), [cargoTypes, data?.cargoTypeId]);

	// CargoTypeEntityDto returns characteristics as a summary list (id+name) but
	// EF doesn't always eager-load the navigation on the server. Use the
	// pre-rendered store map for a reliable source.
	const characteristicIds = useMemo(
		() => store.cargoTypeCharacteristicsMap[cargoType?.id ?? ''] ?? [],
		[cargoType?.id],
	);

	const shouldShow = appConfigStore.cargoTypeRequiresExactWeight(characteristicIds);

	// Minimum the entered exact weight must meet: the cargo type's minimum weight when set,
	// otherwise the smallest weight band in the measurements list. (|| not ?? so a 0/not-found
	// result falls through to the next source.)
	const minimumKg =
		cargoType?.minimumWeight?.value ||
		(cargoType?.minimumWeightId ? getMeasurementValueFromId(cargoType.minimumWeightId) : 0) ||
		findAllMeasurementsForFerryTrip('WEIGHT', 'vehicle')?.[0]?.value ||
		0;
	const minimumInDisplayUnit = minimumKg > 0 ? convertWeight(minimumKg, 'kg', displayUnit) : 0;
	const maximumKg = appConfigStore.vehicleExactWeightMaximumKg;

	useEffect(() => {
		if (!shouldShow) return;
		// Don't overwrite a value the customer has touched.
		if (data.vehicleExactWeightUserEdited) return;

		let prefillKg: number | null = null;
		if (
			cargoType &&
			isNotNullOrUndefined(cargoType.exactWeight) &&
			isNotNullOrUndefined(cargoType.exactWeightUnit)
		) {
			prefillKg = toCanonicalKg(Number(cargoType.exactWeight), parseWeightUnit(cargoType.exactWeightUnit));
		} else if (minimumKg > 0) {
			prefillKg = minimumKg;
		}
		if (prefillKg === null) return;

		const newData = new BookingWizardData({ ...wizardData });
		const inner = newData.innerWizardData as VehicleBookingWizardData;
		inner.vehicleExactWeight = convertWeight(prefillKg, 'kg', displayUnit);
		inner.vehicleExactWeightUnit = displayUnit;
		const bandId = pickWeightBand(prefillKg, findAllMeasurementsForFerryTrip('WEIGHT', 'vehicle') ?? []);
		if (bandId) {
			inner.vehicleWeightId = bandId;
		}
		setWizardData(newData);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldShow, cargoType?.id, displayUnit]);

	if (!isVehicleBookingWizardData(wizardData.innerWizardData) || !shouldShow) {
		return <></>;
	}

	const typedErrors = innerWizardErrors as WizardErrors<VehicleBookingWizardData>;

	const onChangeValue = (value: number | undefined) => {
		const parsed = value ?? null;

		const newData = new BookingWizardData({ ...wizardData });
		const inner = newData.innerWizardData as VehicleBookingWizardData;
		inner.vehicleExactWeight = parsed;
		inner.vehicleExactWeightUnit = parsed === null ? null : displayUnit;
		inner.vehicleExactWeightUserEdited = true;

		// Auto-assign the matching weight band so pricing reflects the entered weight
		// immediately. Server re-derives on save for defence in depth.
		if (parsed !== null && !Number.isNaN(parsed)) {
			const exactKg = toCanonicalKg(parsed, displayUnit);
			const bandId = pickWeightBand(exactKg, findAllMeasurementsForFerryTrip('WEIGHT', 'vehicle') ?? []);
			if (bandId) {
				inner.vehicleWeightId = bandId;
			}
		}

		setWizardData(newData);

		const newErrors = { ...typedErrors };
		const exactKg = parsed !== null && !Number.isNaN(parsed) ? toCanonicalKg(parsed, displayUnit) : null;
		if (parsed === null || Number.isNaN(parsed)) {
			(newErrors as any).vehicleExactWeight = intl.formatMessage({
				id: 'vehicle-tab__exact-weight__required',
				defaultMessage: 'Weight is required',
			});
		} else if (exactKg !== null && exactKg < minimumKg) {
			(newErrors as any).vehicleExactWeight = intl.formatMessage(
				{
					id: 'vehicle-tab__exact-weight__below-min',
					defaultMessage: 'Must be at least {min} {unit}',
				},
				{ min: roundWeightForUnit(minimumInDisplayUnit, displayUnit), unit: weightUnitLabel(displayUnit) },
			);
		} else if (exactKg !== null && maximumKg !== null && exactKg > maximumKg) {
			const maxInDisplay = convertWeight(maximumKg, 'kg', displayUnit);
			(newErrors as any).vehicleExactWeight = intl.formatMessage(
				{
					id: 'vehicle-tab__exact-weight__above-max',
					defaultMessage: 'Must be no more than {max} {unit}',
				},
				{ max: roundWeightForUnit(maxInDisplay, displayUnit), unit: weightUnitLabel(displayUnit) },
			);
		} else if (parsed > EXACT_WEIGHT_MAX_STORABLE) {
			// Hard ceiling: the value is stored as numeric(10,3) in the entered unit,
			// so anything larger overflows the column and fails the save.
			(newErrors as any).vehicleExactWeight = intl.formatMessage(
				{
					id: 'vehicle-tab__exact-weight__above-storable-max',
					defaultMessage: 'Must be no more than {max} {unit}',
				},
				{ max: EXACT_WEIGHT_MAX_STORABLE, unit: weightUnitLabel(displayUnit) },
			);
		} else {
			delete (newErrors as any).vehicleExactWeight;
		}
		setInnerWizardErrors(newErrors);
	};

	const label = intl.formatMessage(
		{
			id: 'vehicle-tab__exact-weight__label',
			defaultMessage: 'Vehicle weight ({unit})',
		},
		{ unit: weightUnitLabel(displayUnit) },
	);

	// Surface a "contact us" line under the input when the entered value
	// exceeds the configured maximum. Translation-driven so operators can
	// customise per language. Two opt-outs: clearing the contact key hides
	// just the supplementary box, clearing the above-max error key hides
	// the box too (operator has opted out of the over-max messaging UX
	// entirely).
	const currentEnteredKg =
		data.vehicleExactWeight != null && !Number.isNaN(data.vehicleExactWeight)
			? toCanonicalKg(data.vehicleExactWeight, displayUnit)
			: null;
	const isAboveMax = currentEnteredKg !== null && maximumKg !== null && currentEnteredKg > maximumKg;
	const contactMessage = intl
		.formatMessage({
			id: 'weight.exactInput.vehicleMaximumExceededContact',
			defaultMessage: 'Please contact us if your vehicle exceeds this limit.',
		})
		.trim();
	const aboveMaxError =
		isAboveMax && maximumKg !== null
			? intl
					.formatMessage(
						{
							id: 'vehicle-tab__exact-weight__above-max',
							defaultMessage: 'Must be no more than {max} {unit}',
						},
						{
							max: roundWeightForUnit(convertWeight(maximumKg, 'kg', displayUnit), displayUnit),
							unit: weightUnitLabel(displayUnit),
						},
					)
					.trim()
			: '';
	const showAboveMaxContact = isAboveMax && aboveMaxError.length > 0 && contactMessage.length > 0;

	// Combined vehicle + trailer cap. When both exact weights are entered and their
	// sum exceeds the configured combined maximum, surface the error inline on this
	// input — mirrored on the trailer input — instead of letting the server reject
	// the booking with an alert toast. Derived live from the shared wizard data so it
	// clears the moment either weight is lowered. (validateVehicleTab enforces the same
	// cap as a hard navigation gate.)
	const combinedMaxKg = appConfigStore.combinedExactWeightMaximumKg;
	const trailerEnteredKg =
		data.trailerExactWeight != null && !Number.isNaN(data.trailerExactWeight)
			? toCanonicalKg(data.trailerExactWeight, displayUnit)
			: null;
	const combinedExceeded =
		combinedMaxKg !== null &&
		currentEnteredKg !== null &&
		trailerEnteredKg !== null &&
		currentEnteredKg + trailerEnteredKg > combinedMaxKg;
	const combinedError =
		combinedExceeded && combinedMaxKg !== null
			? intl.formatMessage(
					{
						id: 'vehicle-tab__combined-exact-weight__above-max',
						defaultMessage: 'Combined vehicle and trailer weight must be no more than {max} {unit}',
					},
					{
						max: roundWeightForUnit(convertWeight(combinedMaxKg, 'kg', displayUnit), displayUnit),
						unit: weightUnitLabel(displayUnit),
					},
				)
			: undefined;

	return (
		<>
			<NumberInput
				className="vehicle-exact-weight"
				label={label}
				placeholder={intl.formatMessage({
					id: 'vehicle-tab__exact-weight__placeholder',
					defaultMessage: 'e.g. 1500',
				})}
				required
				allowNegative={false}
				value={data.vehicleExactWeight ?? undefined}
				error={(typedErrors as any)?.vehicleExactWeight ?? combinedError}
				onChange={onChangeValue}
			/>
			{showAboveMaxContact && (
				<div className="vehicle-exact-weight__above-max-contact">
					<MessageBox status="warning" slim>
						{contactMessage}
					</MessageBox>
				</div>
			)}
		</>
	);
}
